/**
 * Acquisition attribution — where the person came from, captured from the URL.
 *
 * The value is read from the landing URL by this module and kept in
 * localStorage until the account is created, when it is sent along with the
 * sign-up metadata and stored on the profile row. PostHog is not in this path:
 * it attributes per *session*, and the project runs with
 * `person_profiles: 'identified_only'`, so its person-level origin is always
 * empty. This is a separate, first-party capture.
 *
 * Last touch, by product decision: whoever brought the person in most recently
 * before sign-up gets the credit. A visit carrying attribution parameters
 * REPLACES whatever was stored — wholesale, never merged, so a fresh Google
 * click cannot inherit the fbclid of an older Meta click. A visit carrying no
 * parameters at all (direct, bookmark, deep link) leaves the stored value
 * alone, otherwise simply reopening the app would erase the origin.
 *
 * Nothing here may ever throw: attribution is worth less than a sign-up, so
 * every entry point swallows its own failures (private mode, disabled storage,
 * corrupt payload) and degrades to "no attribution".
 */

const STORAGE_KEY = 'sossiety.attribution';

/**
 * Mirrors the columns added to `profiles`. Kept flat and snake_case so the
 * object can be spread straight into the sign-up metadata.
 */
export type Attribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
};

const FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
] as const satisfies readonly (keyof Attribution)[];

/**
 * Matches the column width in the migration. These values reach the database
 * inside client-controlled `raw_user_meta_data`, so the length is capped on the
 * way in as well as in SQL — neither side trusts the other.
 */
const MAX_LENGTH = 255;

type StoredAttribution = Attribution & { captured_at?: string };

const clean = (raw: string | null): string | undefined => {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, MAX_LENGTH);
};

/** Reads the attribution parameters present in a query string, if any. */
const readFromQuery = (search: string): Attribution => {
  const params = new URLSearchParams(search);
  const found: Attribution = {};

  for (const field of FIELDS) {
    const value = clean(params.get(field));
    if (value) found[field] = value;
  }

  return found;
};

/**
 * Captures attribution from the current URL. Call once at boot, before the
 * router has a chance to rewrite the query string.
 */
export const captureAttribution = (): void => {
  try {
    if (typeof window === 'undefined') return;

    const found = readFromQuery(window.location.search);

    // No parameters on this visit: keep whatever brought the person here
    // originally. Only an actual click carrying attribution replaces it.
    if (Object.keys(found).length === 0) return;

    const stored: StoredAttribution = {
      ...found,
      captured_at: new Date().toISOString(),
    };

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Private mode, storage disabled, quota. Not worth a log line: the only
    // consequence is a profile created without attribution.
  }
};

/**
 * The attribution to store on a profile being created now. Returns an empty
 * object when there is nothing to attribute, so the result is always safe to
 * spread into sign-up metadata.
 */
export const getAttribution = (): Attribution => {
  try {
    if (typeof window === 'undefined') return {};

    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};

    const record = parsed as Record<string, unknown>;
    const attribution: Attribution = {};

    // Re-validated on read rather than trusted: the payload survives across
    // deploys and could have been written by an older shape, or edited by hand.
    for (const field of FIELDS) {
      const value = record[field];
      if (typeof value === 'string') {
        const cleaned = clean(value);
        if (cleaned) attribution[field] = cleaned;
      }
    }

    return attribution;
  } catch {
    return {};
  }
};

/** Test and support helper — drops the stored attribution. */
export const clearAttribution = (): void => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do: see captureAttribution.
  }
};
